/**
 * Phase4AuditSentinelBody (extracted from ReleaseOpsTab.jsx in iter84.59.19)
 *
 * Body of the Phase 4 ("Audit · history & sentinels") collapsible
 * section. The wrapper `<CollapsiblePhaseSection number="4" ...>` stays
 * in the orchestrator; only the children live here.
 *
 * iter86.82.2 — Restructured into two operator-named groupings:
 *   4.1 Sync & promotion record — everything you need when naming /
 *       labelling / describing the Sync-to-Main PR:
 *       SyncToMain card (né Canon Sync), Agent Pre-flight Ledger,
 *       Recent Promotions archive.
 *   4.2 Canon decisions — the record of bypass/waiver/stewardship
 *       decisions: Acknowledgement Log, Stewardship Actions.
 * The game-health posture chip was retired from this phase per
 * operator request (posture lives in Product Ops). All cards render
 * with the standard gray outline — no verdict coloring in this
 * audit-only phase.
 */
import { api } from "@/lib/api";
import {
    PreflightAttestationLedgerCard,
    AuditAcknowledgementLogCard,
} from "@/components/site-admin/ReleaseOpsAuditCards";
import { StewardshipActionsCard } from "@/components/site-admin/release-ops/StewardshipActionsCard";
import { PromotionArchiveCard } from "@/components/site-admin/release-ops/PromotionArchiveCard";
import { CanonSyncCard } from "@/components/site-admin/release-ops/CanonSyncCard";
import { SubStep } from "@/components/site-admin/release-ops/Phase1PreCommitSafetyBody";

export function Phase4AuditSentinelBody({
    environment,
    isProd,
    uiMode,
    archivedRows,
    setActivePromotion,
    reloadActivePromotion,
}) {
    return (
        <div className="space-y-6">
            <SubStep n={1} title="Sync & promotion record — what goes into the PR" phase={4} accentClass="text-zinc-400">
                {/* iter86.82 — Sync to Main: the batched dev → main PR
                    ritual (own cadence) that replaced the per-iter
                    pr_approved gate. Preview-only. */}
                {!isProd && <CanonSyncCard />}
                {/* Pre-flight ledger: per-iter attestations the agent wrote
                    before each promotion — source material for the PR
                    title/label/description. Preview-only. */}
                {!isProd && <PreflightAttestationLedgerCard environment={environment} />}
                {/* Promotion archive — operational mode shows live DB list,
                    archive mode (production) shows the GitHub-mediated archive. */}
                <PromotionArchiveCard
                    uiMode={uiMode}
                    rows={archivedRows}
                    onResume={async (id) => {
                        const full = await api.runbookGetPromotion(id);
                        setActivePromotion(full);
                    }}
                    onReload={() => reloadActivePromotion(uiMode)}
                />
            </SubStep>

            <SubStep n={2} title="Canon decisions — waivers & stewardship" phase={4} accentClass="text-zinc-400">
                {/* Acknowledgement log: operator signatures on parity-sweep
                    warnings (signed on preview). */}
                {!isProd && <AuditAcknowledgementLogCard environment={environment} />}
                {/* Stewardship actions: merged Values + Obligations engine
                    audit trails (classifications, dispositions). */}
                <StewardshipActionsCard />
            </SubStep>
        </div>
    );
}
